import { searchIcon } from '@/shared/ui/assets';
import { NoDataComponent, TableComponent, CustomInput } from '@/shared/ui/components';
import { renderSortIcon, sortString } from '@/shared/lib';
import { t } from 'ttag';
import { useReportsTable } from './useReportsTable';
import { REPORT_TYPE_OPTIONS } from './reportsTable.constants';
import {
  actionFormater,
  dateOfServiceFormatter,
  handleSortReportId,
  lastModifiedWithActionFormatter,
  reportIdFormatter,
  reportTypeFormatter,
  statusFormatter,
} from './handler';
import type { ReportsTableProps } from './reportsTable.types';
import './reportsTable.scss';

const ReportsTable = ({
  data,
  isLoadedData,
  shouldLoadVerifiedReport,
  resetShouldLoadVerifiedReport,
}: ReportsTableProps) => {
  const {
    state,
    setState,
    dataReport,
    onClickReport,
    handleTableChange,
    onChangeSearch,
    searchValue,
  } = useReportsTable({ data, shouldLoadVerifiedReport, resetShouldLoadVerifiedReport });

  /**
   * Builds the column set of a study's reports table: report ID, date of service, last modified, status and a row action. Each column renders through its formatter with the current isLoadedData, timezone and study offset, sorting report IDs, last-modified strings and status by a fixed New-to-Completed order, and a report type column is inserted only when the selected report type is Summary reports.
   *
   * @returns Column descriptors for the reports table.
   */
  const generateColumns = () => {
    const columns: any[] = [
      {
        dataIndex: 'reportId',
        title: t`Report ID`,
        sorter: (a: any, b: any) => handleSortReportId(a, b),
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
        render: (...renderProps: any[]) =>
          (reportIdFormatter as any)(...renderProps, {
            isLoadedData,
          }),
      },
      {
        dataIndex: 'dateOfService',
        title: t`Date of service`,
        render: (...renderProps: any[]) =>
          (dateOfServiceFormatter as any)(...renderProps, {
            timezone: data.timezone,
            studyTimeOffset: data.studyTimeOffset,
          }),
      },
      {
        dataIndex: 'lastModified',
        title: t`Last modified`,
        sorter: (a: any, b: any) => a.lastModified.localeCompare(b.lastModified),
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
        render: (...renderProps: any[]) =>
          (lastModifiedWithActionFormatter as any)(...renderProps, {
            isLoadedData,
            timezone: data.timezone,
          }),
      },
    ];

    if (state.reportType === 'Summary reports') {
      columns.push({
        dataIndex: 'type',
        title: t`Report Type`,
        sorter: (a: any, b: any) => sortString(a, b, 'type'),
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
        render: (...renderProps: any[]) =>
          (reportTypeFormatter as any)(...renderProps, {
            isLoadedData,
          }),
      });
    }

    columns.push(
      {
        dataIndex: 'statusReport',
        title: t`Status`,
        sorter: (a: any, b: any) => {
          const statusOrder = [
            'New',
            'Downloaded',
            'Reviewed',
            'Interpreted',
            'Signed',
            'Completed',
          ];
          return statusOrder.indexOf(a.statusReport) - statusOrder.indexOf(b.statusReport);
        },
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
        render: (...renderProps: any[]) =>
          (statusFormatter as any)(...renderProps, {
            isLoadedData,
          }) as any,
      },
      {
        dataIndex: 'action',
        title: t`Actions`,
        render: (...renderProps: any[]) =>
          (actionFormater as any)(...renderProps, {
            onClickReport,
          }),
        width: 48,
      },
    );

    return columns;
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <div className="report-search">
          <div className="list-option">
            {REPORT_TYPE_OPTIONS.map((option) => (
              <div
                onClick={() => {
                  setState({ reportType: option });
                }}
                key={option}
                className={state.reportType === option ? 'active' : ''}
              >
                {option}
              </div>
            ))}
          </div>
          <CustomInput
            className="search-input"
            id="report-search-input"
            name="reportSearch"
            aria-label={`${t`Search by`} ${t`Report ID`}`}
            suffix={<img src={searchIcon} alt="Search icon" />}
            size="middle"
            disabled={!isLoadedData}
            value={searchValue}
            onChange={onChangeSearch}
            placeholder={`${t`Search by`} ${t`Report ID`}`}
          />
        </div>
      </div>
      <div className="custom-body">
        {dataReport.length > 0 || !isLoadedData ? (
          <TableComponent
            isEndOfData
            className="reports-table"
            data={dataReport}
            total={dataReport.length}
            columns={generateColumns()}
            currentPage={state.page}
            pageSize={state.pageSize}
            handleTableChange={handleTableChange}
            isLoading={!isLoadedData}
          />
        ) : (
          <div className="no-data-wrapper">
            <div className="no-data">
              <NoDataComponent />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsTable;
